import React from 'react';
import propTypes from 'prop-types';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import { Search } from 'lucide-react';
import { cn } from 'lib/utils';
import {
  AGENT_FILTER_ALL,
  AGENT_SORT_OPTIONS
} from 'helpers/agencyManagement/agent-filters';
import {
  SEARCH_PLACEHOLDER,
  fieldLabelClass,
  pillSearchSx,
  selectSx
} from './filterFieldStyles';

function sortLabelForValue(value) {
  return (
    AGENT_SORT_OPTIONS.find(option => option.value === value)?.label ?? value
  );
}

/**
 * search, position and sort fields shared by the desktop toolbar and the mobile filters popover
 * @param omitSearch - hides the search field, when it's rendered outside the popover
 * @param stacked - lays the fields out in a column instead of a row
 * @param searchQuery - current search text
 * @param onSearchChange - called with the new search text
 * @param positionFilter - currently selected position, or AGENT_FILTER_ALL
 * @param onPositionFilterChange - called with the newly selected position
 * @param sortOption - currently selected sort option value
 * @param onSortChange - called with the newly selected sort option value
 * @param positionOptions - position labels to offer in the Position dropdown
 */
export default function FilterFields({
  omitSearch = false,
  stacked = false,
  searchQuery,
  onSearchChange,
  positionFilter,
  onPositionFilterChange,
  sortOption,
  onSortChange,
  positionOptions
}) {
  return (
    <div
      className={cn(
        'flex gap-3 w-full justify-start',
        stacked ? 'flex-col' : 'flex-wrap lg:flex-nowrap lg:items-end'
      )}
    >
      {!omitSearch && (
        <div
          className={cn(
            'flex min-w-0 flex-col',
            !stacked &&
              'flex-[0_1_auto] w-[min(100%,360px)] md:w-[min(100%,320px)]'
          )}
        >
          <span className={fieldLabelClass}>Search agents</span>
          <TextField
            size='small'
            placeholder={SEARCH_PLACEHOLDER}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            fullWidth={stacked}
            sx={{
              ...pillSearchSx,
              ...(stacked ? {} : { width: '100%' })
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start' sx={{ mr: 0 }}>
                  <Search
                    className='size-[16px] text-grey shrink-0'
                    aria-hidden
                  />
                </InputAdornment>
              )
            }}
            inputProps={{ 'aria-label': 'Search agents' }}
          />
        </div>
      )}

      <div className='flex min-w-0 flex-col'>
        <span className={fieldLabelClass}>Position</span>
        <TextField
          select
          data-tour='agency-position-filter'
          size='small'
          value={positionFilter}
          onChange={e => onPositionFilterChange(e.target.value)}
          SelectProps={{
            MenuProps: {
              disablePortal: false,
              PaperProps: { 'data-tour': 'agency-position-menu' }
            },
            renderValue: v =>
              v === AGENT_FILTER_ALL ? (
                <span className='text-grey-dark'>All</span>
              ) : (
                v
              )
          }}
          sx={selectSx(stacked)}
        >
          <MenuItem value={AGENT_FILTER_ALL}>All</MenuItem>
          {positionOptions.map(p => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <div className='flex min-w-0 flex-col'>
        <span className={fieldLabelClass}>Sort</span>
        <TextField
          select
          data-tour='agency-sort-filter'
          size='small'
          value={sortOption}
          onChange={e => onSortChange(e.target.value)}
          SelectProps={{
            renderValue: v => sortLabelForValue(v),
            MenuProps: { PaperProps: { 'data-tour': 'agency-sort-menu' } }
          }}
          sx={selectSx(stacked)}
        >
          {AGENT_SORT_OPTIONS.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </div>
    </div>
  );
}

FilterFields.propTypes = {
  omitSearch: propTypes.bool,
  stacked: propTypes.bool,
  searchQuery: propTypes.string.isRequired,
  onSearchChange: propTypes.func.isRequired,
  positionFilter: propTypes.string.isRequired,
  onPositionFilterChange: propTypes.func.isRequired,
  sortOption: propTypes.string.isRequired,
  onSortChange: propTypes.func.isRequired,
  positionOptions: propTypes.arrayOf(propTypes.string).isRequired
};
